/* istanbul ignore file */

import { Injectable } from '@angular/core';
import { Firestore } from '@angular/fire/firestore';

import {
  ChannelWithDocId,
  getActivePage as getChannelPage,
} from '@yoimo/client-sdk/channels';
import {
  EventSourceQueryOption,
  EventWithDocId,
  getEventsFromSource,
  getTickets,
} from '@yoimo/client-sdk/events';
import { getActivePage as getEventPage } from '@yoimo/client-sdk/events';
import { Branding, EventListSource, Ticket } from '@yoimo/interfaces';

import {
  combineLatest,
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
} from 'rxjs';

import { PurchaseOption } from '@core/interfaces/types';

import { AccessControlService } from './access-control.service';

@Injectable({ providedIn: 'root' })
export class EventsService {
  constructor(
    private fs: Firestore,
    private accessControlService: AccessControlService
  ) {}

  getEvents$(
    source: EventListSource,
    options: EventSourceQueryOption,
    until$?: Subject<void>
  ): Observable<EventWithDocId[]> {
    const events$ = getEventsFromSource(this.fs, source, options);

    if (until$) events$.pipe(takeUntil(until$));

    return events$;
  }

  getEventBranding(
    channel: ChannelWithDocId,
    event: EventWithDocId
  ): Partial<Branding> {
    return {
      ...channel?.branding,
      ...getChannelPage(channel)?.brandingOverride,
      ...getEventPage(event)?.brandingOverride,
    };
  }

  getApplicableProducts$(
    channelId: string,
    event: EventWithDocId
  ): Observable<PurchaseOption[]> {
    return combineLatest([
      this.accessControlService.getApplicablePlans$(channelId, event),
      of(
        getTickets(event, {
          excludeExpired: true,
          includeTicketsWithLimitedRedemptions: false,
        })
      ),
    ]).pipe(
      take(1),
      map(([plans, tickets]) => {
        const options: PurchaseOption[] = plans.map((p) => {
          return { plan: p };
        });
        options.push(
          ...tickets.map((t) => {
            return { ticket: t, event: event };
          })
        );
        return options;
      })
    );
  }
}
